import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSchoolTransactions } from "../../DataStore/FeesTransactionContext";
import ActivityLoader from "../Common/ActivityLoader";
import Notification from "../Common/Notification";
import functions from "../../util/functions";
import { useCurrency } from "../../util/SystemVariables";

const billMeta = (billingType) => {
  const t = Number(billingType || 0);
  if (t === 1) return { label: "Invoice", icon: "fa-file-invoice" };
  if (t === 2) return { label: "Discount", icon: "fa-tag" };
  return { label: "Payment", icon: "fa-receipt" };
};

const groupByDate = (arr) =>
  arr.reduce((acc, it) => {
    const d = String(it?.trans_date || "");
    (acc[d] = acc[d] || []).push(it);
    return acc;
  }, {});

const RecentFeesTransactions = () => {
  const [showAll, setShowAll] = useState(false);
  const currency = useCurrency();
  const { getRecentTransactions, recentTransactions } = useSchoolTransactions();

  useEffect(() => {
    getRecentTransactions();
  }, []);

  const max = 4;

  const items = useMemo(() => {
    if (!Array.isArray(recentTransactions)) return [];
    // sort latest first
    return [...recentTransactions].sort((a, b) =>
      String(b?.trans_date || "").localeCompare(String(a?.trans_date || "")) ||
      String(b?.ledger_id || "").localeCompare(String(a?.ledger_id || ""))
    );
  }, [recentTransactions]);

  const visible = showAll ? items : items.slice(0, max);
  const groups = useMemo(() => groupByDate(visible), [visible]);
  const dates = useMemo(() => Object.keys(groups).sort((a, b) => b.localeCompare(a)), [groups]);

  const extraCount = Math.max(0, items.length - max);

  if (!recentTransactions) {
    return (
      <div className="card rac-card mb-3">
        <div className="rac-head d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center" style={{ gap: 12 }}>
            <div className="rac-headIcon">
              <i className="fa fa-clock" />
            </div>
            <div>
              <h6 className="rac-title">Recent Fees Transactions</h6>
              <div className="rac-sub">Latest payments and entries posted</div>
            </div>
          </div>
          <span className="rac-action" style={{ opacity: 0.6 }}>
            View all <i className="fa fa-arrow-right" />
          </span>
        </div>

        <div className="p-4">
          <ActivityLoader />
        </div>
      </div>
    );
  }

  if (recentTransactions === "404") {
    return (
      <div className="card rac-card mb-3">
        <div className="rac-head d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center" style={{ gap: 12 }}>
            <div className="rac-headIcon">
              <i className="fa fa-clock" />
            </div>
            <div>
              <h6 className="rac-title">Recent Fees Transactions</h6>
              <div className="rac-sub">Latest payments and entries posted</div>
            </div>
          </div>
          <Link to="/school-fees-transactions" className="rac-action">
            View all <i className="fa fa-arrow-right" />
          </Link>
        </div>

        <div className="p-4">
          <Notification message="No recent school fees transaction found for this batch." closable={false} />
        </div>
      </div>
    );
  }

  return (
    <div className="card rac-card mb-3">
      {/* Header */}
      <div className="rac-head d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center" style={{ gap: 12 }}>
          <div className="rac-headIcon">
            <i className="fa fa-clock" />
          </div>
          <div>
            <h6 className="rac-title">Recent Fees Transactions</h6>
            <div className="rac-sub">Latest payments and entries posted</div>
          </div>
        </div>

        <Link to="/school-fees-transactions" className="rac-action">
          View all <i className="fa fa-arrow-right" />
        </Link>
      </div>

      {/* Body */}
      <div className="card-body p-4">
        <div className="overflow-auto" style={{ maxHeight: 560 }}>
          {dates.map((date) => (
            <div key={date} className="mb-3">
              <div className="rac-date">{functions.formatDate(date)}</div>

              <ul className="list-unstyled mb-0">
                {groups[date].map((item) => {
                  const meta = billMeta(item?.billing_type);
                  const amount = Number(item?.amount || 0);

                  // from (bank/cash) => to (fees receivable)
                  const fromAcc = item?.ledger?.ledger_grouping?.account;
                  const toAcc = item?.account;

                  const fromCode = fromAcc?.account_number || "";
                  const fromName = fromAcc?.account_name || "Cash/Bank";
                  const toCode = toAcc?.account_number || "";
                  const toName = toAcc?.account_name || "Fees account";

                  const ref = item?.ledger?.transaction_ref || "";
                  const memo = item?.description || "";

                  // Payment / Invoice / Discount -> indigo chip (no green)
                  const isOk = true;

                  return (
                    <li key={item?.ledger_id} className="py-2">
                      <div className="rac-row">
                        <div className="rac-doc">
                          <i className={`fa ${meta.icon}`} />
                        </div>

                        <div className="ml-3 flex-grow-1" style={{ minWidth: 0 }}>
                          <div className="rac-titleline">
                            <span className="rac-code">{fromCode || "—"}</span>
                            <span className="rac-acc">{fromName}</span>
                            <i className="fa fa-arrow-right mx-2 text-muted" style={{ opacity: 0.45 }} />
                            <span className="rac-code">{toCode || "—"}</span>
                            <span className="rac-acc">{toName}</span>
                          </div>

                          <div className="rac-memo">{memo}</div>

                          <div className="mt-2 d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
                            {!!ref && <span className="rac-ref text-monospace">{ref}</span>}

                            <span className={`rac-pill ${isOk ? "is-ok" : "is-bad"}`}>
                              <i className="fa fa-dot-circle" />
                              {meta.label}
                            </span>

                            <span className="rac-amtChip">
                              {currency} {functions.numberFormat(amount)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <hr className="my-3" />
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="rac-foot">
        <div className="text-muted" style={{ fontSize: 12 }}>
          Showing <strong>{visible.length}</strong> of <strong>{items.length}</strong>
        </div>

        {items.length > max && (
          <button type="button" className="rac-btn" onClick={() => setShowAll((s) => !s)}>
            {showAll ? "Show less" : `Show ${extraCount} more`}
          </button>
        )}
      </div>
    </div>
  );
};

export default RecentFeesTransactions;
